import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/hooks/useAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { AnimatedPyramidLogo } from '@/components/landing/AnimatedLedgerLoop';
import { logger } from '@/lib/logger';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Get initial tab from URL params (mode=signup or mode=signin)
  const initialTab = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Handle cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleMagicLink = async (e: React.FormEvent, isSignUp: boolean = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call our custom edge function instead of Supabase's signInWithOtp
      const { data, error } = await supabase.functions.invoke('send-magic-link', {
        body: {
          email,
          fullName: isSignUp ? fullName : undefined,
          isSignUp,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to send magic link');

      setMagicLinkSent(true);
      setResendCooldown(60);

      logger.info('Magic link sent', { meta: { email, isSignUp } });
      logger.audit('Magic link requested', {
        meta: { email, action: isSignUp ? 'signup' : 'signin' },
      });

      toast.success('Check your email for your magic link!');
    } catch (error: any) {
      logger.error('Magic link error', error, { meta: { email } });
      toast.error(error.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (resendCooldown === 0) {
      setMagicLinkSent(false);
    }
  };

  // Show magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <AnimatedPyramidLogo variant="auth" />
            </div>
            <CardTitle className="text-2xl font-semibold text-center">Check Your Email</CardTitle>
            <CardDescription className="text-center">
              We've sent a magic link to <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground text-center space-y-2">
              <p>Click the link in the email to sign in securely.</p>
              <p>The link expires in 60 minutes.</p>
            </div>
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground text-center mb-3">
                Didn't receive the email? Check your spam folder.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleResend}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Magic Link'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <AnimatedPyramidLogo variant="auth" />
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              <TabsContent value="signin">
                <form onSubmit={(e) => handleMagicLink(e, false)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Magic Link'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    We'll email you a secure link to sign in instantly
                  </p>
                </form>
              </TabsContent>
              <TabsContent value="signup">
                <form onSubmit={(e) => handleMagicLink(e, true)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Magic Link'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    No password needed—we'll email you a secure sign-up link
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
